import React, { useEffect, useRef } from "react"
import PropTypes from "prop-types"
import styles from "./Header.module.css"
import bgMan from "assets/imagesV2/logo_man.svg"
import bgWoman from "assets/imagesV2/logo_woman.svg"
import { useTheme } from "context/AntdThemeContext"
import { themes } from "constants/theme"
import { Avatar, Button, Flex } from "antd"
import { useTranslation } from "react-i18next"
import { Link, useMatch, useNavigate, useSearchParams } from "react-router-dom"
import { useDispatch, useSelector } from "react-redux"
import { useCable } from "components/Cable"
import { signOut } from "store/auth"
import { resetApp } from "store/actions"
import { Icon } from "ui/Icon"
import { receivedWSConversation } from "store/conversations"
import { setProHomeMenuOpen } from "store/pro-home-menu"
import { routes } from "router"
import { channels } from "constants/cable"
import { colors } from "themes/variables"
import classNames from "classnames"
import { useConfirmModal } from "modules/modals/hooks/useConfirmModal"
import { AiIcon } from "ui/Icon/DSIcon/AiIcon"
import { Booking } from "ui/Icon/DSIcon/Booking"
import { openChatCard } from "store/aiassistant"
import useActiveGuide from "hooks/useActiveClient"
import { isFeatureEnabled } from "config/features"

export const ProHeaderUI = ({ hideOnMobile = false, embedded = false }) => {
  const [searchParams] = useSearchParams()
  const conversationId = searchParams.get("conversation_id")
  const { t } = useTranslation()
  const { theme } = useTheme()
  const cable = useCable()
  const dispatch = useDispatch()
  const { avatar, verified } = useSelector((state) => state.user)
  const logoSrc = theme === themes.sportswoman ? bgWoman : bgMan
  const chanel = useRef(null)
  const messagesBoxScrollSaver = useRef(null)
  const isProWizardPage = useMatch({ path: routes.proPersonalInfoRootPath(), end: false })
  const confirmModal = useConfirmModal()
  const navigate = useNavigate()
  const { isActive: active } = useActiveGuide()

  const logoutBtnClickHandler = confirmModal(
    { title: t("global.confirm_sign_out_text"), color: "danger", submitText: t("global.confirm") },
    () =>
      dispatch(signOut()).then(() => {
        dispatch(resetApp())
        navigate(routes.rootPath())
      })
  )

  const menuBtnClickHandler = () => {
    dispatch(setProHomeMenuOpen())
  }

  const beforeAction = (action) => {
    if (action.type === receivedWSConversation.type && messagesBoxScrollSaver.current) messagesBoxScrollSaver.current.saveScroll()
  }

  const afterAction = (action, response) => {
    if (action.type === receivedWSConversation.type && messagesBoxScrollSaver.current)
      messagesBoxScrollSaver.current.scrollToSaved((savedScrolls) => (savedScrolls.bottom === 0 ? "bottom" : "top"))
  }

  const receivedHandler = (beforeAction, afterAction) => (action) => {
    beforeAction?.(action)
    const dispatchResult = dispatch({
      ...action,
      payload: {
        ...action.payload,
        conversationId
      }
    })

    if (dispatchResult instanceof Promise) {
      dispatchResult.then((res) => afterAction(action, res))
    } else {
      afterAction(action)
    }
  }

  useEffect(() => {
    if (!cable || [false, "false"].includes(process.env.REACT_APP_WS_ON)) return
    chanel.current = cable.subscriptions.create({ channel: channels.rootChannel }, { received: receivedHandler(beforeAction, afterAction) })
    return () => chanel.current?.unsubscribe()
  }, [cable])

  return (
    <div className={classNames(styles.header, hideOnMobile && isProWizardPage && styles.hideOnMobile)}>
      <Icon onClick={menuBtnClickHandler} className={styles.menuIcon} name="menu" color="currentColor" />
      <Link to={routes.guideDashboardPath()} className={styles.logo}>
        <img className={classNames(styles.logoImage, embedded && styles.embeddedHeaderLogo)} src={logoSrc} alt="" />
      </Link>
      <Flex align="center" className={styles.actions}>
        <Button
          type="primary"
          size="large"
          style={{ display: "flex", justifyContent: "center", alignItems: "center" }}
          icon={<Booking fill={colors.white} height={20} width={20} />}
          aria-label={t("bookings.add")}
          onClick={() => verified && navigate(routes.guideBookingNewPath())}
          disabled={!verified}
        >
          <span className={styles.newBookingBtnTxt}>{t("bookings.add")}</span>
        </Button>
        {isFeatureEnabled("AI_DASHBOARD") && (
          <Button
            type="primary"
            size="large"
            icon={<AiIcon fill={colors.white} height={20} width={20} />}
            aria-label="AI Assistant"
            className={styles.aiAssistantButton}
            onClick={active ? () => dispatch(openChatCard()) : undefined}
            disabled={!active}
          >
            <span className={styles.aiAssistanceBtnTxt}>AI Assistant</span>
          </Button>
        )}
        <Button className={styles.logoutBtn} color="primary" variant="outlined" size="large" onClick={logoutBtnClickHandler}>
          {t("header.log_out")}
        </Button>
        <Link to={routes.proSettingsProfileInformation()}>
          <Avatar
            src={avatar?.includes("localhost") ? avatar.replace("https://", "http://") : avatar}
            style={{
              border: `1.5px solid ${colors.lines}`,
              flexShrink: 0
            }}
            className={styles.avatar}
          />
        </Link>
      </Flex>
    </div>
  )
}

ProHeaderUI.propTypes = {
  hideOnMobile: PropTypes.bool,
  embedded: PropTypes.bool
}
